import type { BootstrapOrganizationResponse } from '@laplace/contracts'

type BootstrapOrganizationRole = BootstrapOrganizationResponse['role']

export function canManageOrganizationAsOwner(
  role: BootstrapOrganizationRole | undefined,
): boolean {
  return role === 'OWNER' || role === 'ADMIN'
}

export function isOrganizationOwner(
  role: BootstrapOrganizationRole | undefined,
): boolean {
  return role === 'OWNER'
}

/**
 * Whether the signed-in user owns the bootstrap organization. The bootstrap
 * response does not carry `role` today (only `members`, roles lowercase), so
 * this falls back to the viewer's membership entry.
 */
export function isBootstrapOrganizationOwner(
  bootstrap:
    | {
        user: { id: string }
        organization: {
          role?: string
          members?: Array<{ userId: string; role: string }>
        } | null
      }
    | null
    | undefined,
): boolean {
  const organization = bootstrap?.organization
  if (!organization) return false
  if (organization.role) return organization.role.toUpperCase() === 'OWNER'
  return (
    organization.members?.some(
      (member) =>
        member.userId === bootstrap.user.id &&
        member.role.toUpperCase() === 'OWNER',
    ) ?? false
  )
}

/**
 * Agents and automations are organization-owned and the API admits any
 * active member, including the personal (TECHNICAL) organization that GTM
 * sign-ups get. `isVisible` only means "show the organization as a team".
 */
export function canUseOrganizationWorkspaceTools(
  organization:
    | Pick<BootstrapOrganizationResponse, 'membershipStatus'>
    | null
    | undefined,
): boolean {
  return organization?.membershipStatus === 'ACTIVE'
}
